import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  applyChoice,
  closePanel,
  getState,
  markNoticeSeen,
  subscribe,
} from '../../helpers/consent';

/**
 * The privacy choices banner (plan section 3.6). Renders null on the server
 * AND on the first client render — `mounted` starts false, matching exactly
 * what the server sent, so hydration never sees a mismatch (O13: the SSR
 * output must never depend on consent state, because the edge cache is
 * shared across visitors).
 *
 * Not modal: no overlay, no focus trap; the page underneath stays usable
 * while it is shown. Reopening always works, in either region, via
 * window.ppConsent.open() — the footer link and the notice page's button,
 * neither of which can reach React state directly, which is why that hook
 * lives on the consent.js runtime instead (see its contract).
 *
 * Two regimes, and the difference between them is legal, not cosmetic:
 *
 *   OPT-IN (EEA/UK/CH, 32 territories) — shown on every page load until the
 *   visitor actually decides. It cannot be dismissed, Escape is inert, and
 *   `pp_notice` is ignored: silence is not consent (GDPR recital 32), so
 *   there is no number of showings after which we may stop asking. It also
 *   renders in the `--prominent` variant, larger and lifted off the bottom
 *   edge, to maximise the chance of ANY answer. Not of a particular answer:
 *   Accept and Decline share one class so they stay identical in size,
 *   weight and colour, which is what keeps this the right side of the
 *   EDPB's deceptive-design guidance.
 *
 *   OPT-OUT (everywhere else) — shown exactly once per browser, then never
 *   again unless reopened. Nothing is lost by that: analytics is already
 *   granted by default there, so a visitor who never answers is counted
 *   either way, and the two controls the law actually requires (the notice
 *   link and Do Not Sell or Share) live permanently in the footer.
 *
 * A stored decision outranks both regimes and both defaults, always.
 */
const CookieNotice = () => {
  const [mounted, setMounted] = useState(false);
  const [state, setState] = useState(null);
  const [showManage, setShowManage] = useState(false);
  const [draft, setDraft] = useState({ a: false, p: false });
  const headingRef = useRef(null);
  const sectionRef = useRef(null);
  const wasOpen = useRef(false);
  const suppressed = useRef(false);

  useEffect(() => {
    const initial = getState();
    // Snapshot taken BEFORE marking, and held in a ref so later renders keep
    // reading it: the one showing that writes `pp_notice` still has to be
    // seen, so the decision to hide is taken from the state this browser
    // arrived with, never from the state this effect goes on to create.
    suppressed.current =
      !initial.stored && !initial.optIn && initial.noticeSeen;
    setMounted(true);
    setState(initial);
    if (!initial.stored && !initial.optIn && !initial.noticeSeen) {
      markNoticeSeen();
    }
    return subscribe(setState);
  }, []);

  // One predicate for both the rendering and the body padding. They used to
  // be computed separately, which would now leave a repeat visitor with the
  // padding of a banner that is not on screen.
  const shouldShow =
    !!state && (!!state.open || (!state.stored && !suppressed.current));

  // Body padding (so the fixed banner never permanently hides the footer's
  // legal row — P9) and the one-time focus move for a REOPENED panel. Never
  // fires on the implicit first-visit show, only when `open` transitions
  // false -> true, which only happens via an explicit openPanel() call.
  useEffect(() => {
    if (!state) return undefined;
    const showing = shouldShow;
    document.body.classList.toggle('pp-consent-open', showing);
    const measure = () => {
      if (showing && sectionRef.current) {
        // Distance from the banner's top edge down to the bottom of the
        // viewport — NOT offsetHeight. The prominent variant sits lifted off
        // the bottom, and the page has to clear the gap underneath it as
        // well as the card itself.
        const rect = sectionRef.current.getBoundingClientRect();
        document.documentElement.style.setProperty(
          '--pp-consent-h',
          `${Math.round(window.innerHeight - rect.top)}px`,
        );
      }
    };
    measure();
    window.addEventListener('resize', measure);
    if (state.open && !wasOpen.current && headingRef.current) {
      headingRef.current.focus();
    }
    wasOpen.current = state.open;
    return () => {
      document.body.classList.remove('pp-consent-open');
      window.removeEventListener('resize', measure);
    };
  }, [state, shouldShow, showManage]);

  const visible = !!mounted && shouldShow;

  // Escape dismisses a REOPENED panel only — a first-time visitor with no
  // stored choice cannot Escape past the notice without choosing one. A
  // document-level listener (rather than onKeyDown on the section) so it
  // works regardless of focus location, and so the section itself stays a
  // plain non-interactive landmark for the a11y tooling.
  useEffect(() => {
    if (!visible || !state || !state.stored) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowManage(false);
        closePanel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, state]);

  if (!visible) return null;

  // getState().effective is {analytics, ads}; the draft the checkboxes read is
  // {a, p}. Seeding one from the other verbatim left both boxes undefined ->
  // unchecked, so Manage always opened showing "off" whatever the visitor had
  // chosen, and saving without touching a box silently revoked consent.
  const openManage = () => {
    setDraft({ a: state.effective.analytics, p: state.effective.ads });
    setShowManage(true);
  };

  const finish = (choice) => {
    applyChoice(choice);
    setShowManage(false);
    closePanel();
  };

  // Prominent only while an opt-in visitor still owes us a decision. Once
  // they have one stored, a reopened panel is a settings dialog, not a
  // prompt, and does not need to shout.
  const prominent = !!state.optIn && !state.stored;

  const secondSentence = state.optIn
    ? 'Until you choose, we set no cookies other than the one that remembers your choice.'
    : 'Analytics is on by default in your region; you can turn it off here at any time.';

  return (
    <section
      // `cookie-consent` carries no style. The TAIUIT privacy audit decides
      // whether a banner exists by matching the id or class of a div/section
      // against /cookie.?consent|cookie.?banner|cookie.?notice|.../, and
      // `pp-consent` matches none of them (BL-0157).
      className={`pp-consent cookie-consent${
        prominent ? ' pp-consent--prominent' : ''
      }`}
      aria-labelledby="pp-consent-title"
      ref={sectionRef}
    >
      <div className="pp-consent__inner">
        <h2
          id="pp-consent-title"
          className="pp-consent__title"
          tabIndex={-1}
          ref={headingRef}
        >
          Your privacy choices
        </h2>
        <p className="pp-consent__body">
          This archive uses Google Analytics to understand how it is used and,
          only if you allow it, Google signals for advertising measurement.{' '}
          {secondSentence} Your choice applies to all three Doxcelerate
          archives.{' '}
          <Link to="/privacy" className="pp-consent__link">
            Privacy notice
          </Link>
        </p>

        {!showManage ? (
          <div className="pp-consent__actions">
            <button
              type="button"
              className="pp-consent__btn"
              onClick={() => finish({ a: true, p: true })}
            >
              Accept all
            </button>
            <button
              type="button"
              className="pp-consent__btn"
              onClick={() => finish({ a: false, p: false })}
            >
              Decline
            </button>
            <button
              type="button"
              className="pp-consent__btn"
              onClick={openManage}
            >
              Manage
            </button>
          </div>
        ) : (
          <div className="pp-consent__manage">
            <label className="pp-consent__option">
              <input
                type="checkbox"
                checked={draft.a}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, a: e.target.checked }))
                }
              />
              <span>
                <strong>Analytics</strong> — lets us see which pages and
                searches are used, in aggregate.
              </span>
            </label>
            <label className="pp-consent__option">
              <input
                type="checkbox"
                checked={draft.p}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, p: e.target.checked }))
                }
              />
              <span>
                <strong>Advertising</strong> — lets Google link your visit to
                advertising measurement.
              </span>
            </label>
            {state.gpc ? (
              <p className="pp-consent__gpc">
                Your browser sends a Global Privacy Control signal, so
                Advertising is off unless you turn it on here.
              </p>
            ) : null}
            <div className="pp-consent__actions">
              <button
                type="button"
                className="pp-consent__btn"
                onClick={() => finish(draft)}
              >
                Save choices
              </button>
              <button
                type="button"
                className="pp-consent__btn"
                onClick={() => setShowManage(false)}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CookieNotice;
